import React, { useState, Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { BtcPriceProvider } from './contexts/BtcPriceContext';
import { SocketProvider } from './contexts/SocketContext';
import { LoginModalProvider } from './contexts/LoginModalContext';
import { SupportChatProvider } from './contexts/SupportChatContext';
import { LiveChatProvider } from './contexts/LiveChatContext';
import { BetSlipProvider } from './contexts/BetSlipContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';
import { autoDetectAndSetLanguage } from './utils/geoLanguageDetector';
import './i18n';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import BottomNavigation from './components/BottomNavigation';
import LandingPage from './pages/LandingPage';
import NotificationContainer from './components/NotificationContainer';
import CustomerSupportChat from './components/CustomerSupportChat';
import LiveChatPanel from './components/LiveChatPanel';
import BonusClaimModal from './components/BonusClaimModal';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './contexts/AuthContext';
import { useSocketEvents } from './contexts/SocketContext';
import { invalidateBalanceQueries } from './services/moneyService';
import { useLiveChat } from './contexts/LiveChatContext';
import { useNotifications } from './hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import './App.css';
import { usePageTracking } from './hooks/useGoogleAnalytics';
import googleAnalytics from './services/googleAnalyticsService';

// Google OAuth Client ID - should be set in environment variable
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

// Retry lazy import on ChunkLoadError (e.g. after dev server recompile) by reloading the page
function lazyWithChunkRetry<T extends React.ComponentType<any>>(
  importFn: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await importFn();
    } catch (err: any) {
      if (err?.name === 'ChunkLoadError' || err?.message?.includes?.('Loading chunk')) {
        window.location.reload();
      }
      throw err;
    }
  });
}

// Lazy load pages for code splitting - significantly reduces initial bundle size
const DepositPage = lazyWithChunkRetry(() => import('./pages/DepositPage'));
const WithdrawPage = lazyWithChunkRetry(() => import('./pages/WithdrawPage'));
const BetHistoryPage = lazyWithChunkRetry(() => import('./pages/BetHistoryPage'));
const MyPage = lazyWithChunkRetry(() => import('./pages/MyPage'));
const NoticePage = lazyWithChunkRetry(() => import('./pages/NoticePage'));
const LiveCasinoPage = lazyWithChunkRetry(() => import('./pages/LiveCasinoPage'));
const SlotGamesPage = lazyWithChunkRetry(() => import('./pages/SlotGamesPage'));
const RoulettePage = lazyWithChunkRetry(() => import('./pages/RoulettePage'));
const CouponPage = lazyWithChunkRetry(() => import('./pages/CouponPage'));
const DailyCheckPage = lazyWithChunkRetry(() => import('./pages/DailyCheckPage'));
const MessagePage = lazyWithChunkRetry(() => import('./pages/MessagePage'));
const EventPage = lazyWithChunkRetry(() => import('./pages/EventPage'));
const QuestionPage = lazyWithChunkRetry(() => import('./pages/QuestionPage'));
const MinigameBuiltinPage = lazyWithChunkRetry(() => import('./pages/MinigameBuiltinPage'));
const MinigameFreePage = lazyWithChunkRetry(() => import('./pages/MinigameFreePage'));
const MinigameBtcGraphPage = lazyWithChunkRetry(() => import('./pages/MinigameBtcGraphPage'));
const MinigameGamePage = lazyWithChunkRetry(() => import('./pages/MinigameGamePage'));
const GamePlayPage = lazyWithChunkRetry(() => import('./pages/GamePlayPage'));
const EmailVerificationPage = lazyWithChunkRetry(() => import('./pages/EmailVerificationPage'));
const SignupCompletePage = lazyWithChunkRetry(() => import('./pages/SignupCompletePage'));
const RuntimeConfigTest = lazyWithChunkRetry(() => import('./components/RuntimeConfigTest'));
const DepositSuccessPage = lazyWithChunkRetry(() => import('./pages/DepositSuccessPage'));
const DepositCancelPage = lazyWithChunkRetry(() => import('./pages/DepositCancelPage'));
const BlogPage = lazyWithChunkRetry(() => import('./pages/BlogPage'));
const WalletPage = lazyWithChunkRetry(() => import('./pages/WalletPage'));
const BonusPage = lazyWithChunkRetry(() => import('./pages/BonusPage'));
const CatchCoinPage = lazyWithChunkRetry(() => import('./pages/CatchCoinPage'));
const SportsBettingPage = lazyWithChunkRetry(() => import('./pages/SportsBettingPage'));
const LiveSportsBettingPage = lazyWithChunkRetry(() => import('./pages/LiveSportsBettingPage'));
const EsportsBettingPage = lazyWithChunkRetry(() => import('./pages/EsportsBettingPage'));
const LiveEsportsBettingPage = lazyWithChunkRetry(() => import('./pages/LiveEsportsBettingPage'));
const EventDetailPage = lazyWithChunkRetry(() => import('./pages/EventDetailPage'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh] p-8">
    <div className="w-full max-w-md space-y-4">
      <div className="h-8 w-48 bg-white/10 rounded animate-pulse" />
      <div className="space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-4 bg-white/10 rounded animate-pulse" style={{ width: `${90 - i * 10}%` }} />
        ))}
      </div>
    </div>
  </div>
);

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/** Wraps SupportChatProvider with auth from AuthContext to avoid circular dependency. */
function AppSupportChatWrapper({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isAuthInitialized } = useAuth();
  return (
    <SupportChatProvider
      auth={{
        user: user ? { id: user.id, name: user.name, email: user.email } : null,
        isAuthenticated,
        isAuthInitialized,
      }}
    >
      {children}
    </SupportChatProvider>
  );
}

function AppContent() {
  const queryClient = useQueryClient();
  const { user, refreshUserData, logout, isAuthenticated } = useAuth();
  const { onUserInfoUpdate, onForceLogout, on, off } = useSocketEvents();
  const { isOpen: isLiveChatOpen } = useLiveChat();
  const { success, warning } = useNotifications();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  
  // Track all page views with Google Analytics
  usePageTracking();

  // Capture ?ref= (and similar) for affiliate attribution on deposit
  useEffect(() => {
    googleAnalytics.captureAffiliateRefFromUrl();
  }, []);

  // Auto-detect language based on user's IP location (runs once on first visit)
  useEffect(() => {
    autoDetectAndSetLanguage((lang) => i18n.changeLanguage(lang));
  }, [i18n]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarActiveCategory, setSidebarActiveCategory] = useState('');
  const [headerActiveButton, setHeaderActiveButton] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const publicLoginSentTimeRef = React.useRef<number>(0);

  // Track when we send PublicLogin to avoid false positives
  React.useEffect(() => {
    const handlePublicLoginSent = (data: any) => {
      if (data && data.timestamp) {
        publicLoginSentTimeRef.current = data.timestamp;
      }
    };

    on('publicLoginSent', handlePublicLoginSent);

    return () => {
      off('publicLoginSent', handlePublicLoginSent);
    };
  }, [on, off]);
  
  // Check if mobile on mount and resize
  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Lock body scroll on mobile when sidebar is open
  useBodyScrollLock(isSidebarOpen && isMobile);

  // Listen to WebSocket userInfo updates to refresh balance
  React.useEffect(() => {
    const handleUserInfoUpdate = async (data: any) => {
      try {
        // Update stored user data from WebSocket
        if (data && data.user) {
          // Refresh user data from API to get latest balance
          await refreshUserData();
          // Also trigger a page refresh for components that read from localStorage
          window.dispatchEvent(new Event('userBalanceUpdated'));
        }
      } catch (error) {
        console.error('Error updating user info from WebSocket:', error);
      }
    };

    onUserInfoUpdate(handleUserInfoUpdate);

    return () => {
      off('userInfo', handleUserInfoUpdate);
    };
  }, [onUserInfoUpdate, off, refreshUserData]);

  // Initialize realtime service for logged-in users (Socket.IO for instant updates)
  React.useEffect(() => {
    if (!user?.id) return;

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;

    // Import dynamically to avoid circular dependencies
    import('./services/realtimeService').then(({ default: realtimeService, EVENTS }) => {
      if (cancelled) return;

      realtimeService.connect(user.id);

      // Global handler for withdrawal processed (works on any page)
      const handleWithdrawalProcessed = async (data: { amount: number; status: string; newBalance: number }) => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[App] Withdrawal processed via socket:', data);
        }

        const now = Date.now();
        const last = (window as any).__lastWithdrawalNotify;
        const key = `${data.amount}-${data.status}`;
        if (last && last.key === key && now - last.time < 10000) return;
        (window as any).__lastWithdrawalNotify = { key, amount: data.amount, time: now };

        if (data.status === 'approved') {
          success('Withdrawal Approved! ✅', `Your withdrawal of $${data.amount.toFixed(2)} has been approved and is being processed.`);
          googleAnalytics.trackWithdrawal(data.amount, 'USD', 'crypto');
        } else if (data.status === 'rejected') {
          warning('Withdrawal Rejected', `Your withdrawal of $${data.amount.toFixed(2)} was rejected. Your balance has been refunded.`);
        }

        await refreshUserData();
        window.dispatchEvent(new Event('userBalanceUpdated'));
      };

      // Dedupe: only show one deposit notification per amount within 10 seconds (handles duplicate backend emits)
      const handleDepositConfirmed = async (data: { amount: number; bonusAmount?: number; transactionId?: string }) => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[App] Deposit confirmed via socket:', data);
        }
        const now = Date.now();
        const last = (window as any).__lastDepositNotify;
        if (last && last.amount === data.amount && now - last.time < 10000) {
          return; // skip duplicate
        }
        (window as any).__lastDepositNotify = { amount: data.amount, time: now };

        const bonusText = data.bonusAmount ? ` (+$${data.bonusAmount.toFixed(2)} bonus)` : '';
        success('Deposit Confirmed! 🎉', `$${data.amount.toFixed(2)}${bonusText} has been credited to your account`);

        googleAnalytics.trackDeposit(data.amount, 'USD', 'crypto', googleAnalytics.getAffiliateRef());

        await refreshUserData();
        window.dispatchEvent(new Event('userBalanceUpdated'));
      };

      const handleBalanceUpdated = async () => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[App] Balance updated via socket');
        }
        await refreshUserData();
        invalidateBalanceQueries(queryClient);
        window.dispatchEvent(new Event('userBalanceUpdated'));
      };

      realtimeService.on(EVENTS.WITHDRAWAL_PROCESSED, handleWithdrawalProcessed);
      realtimeService.on(EVENTS.DEPOSIT_CONFIRMED, handleDepositConfirmed);
      realtimeService.on(EVENTS.BALANCE_UPDATED, handleBalanceUpdated);

      unsubscribe = () => {
        realtimeService.off(EVENTS.WITHDRAWAL_PROCESSED, handleWithdrawalProcessed);
        realtimeService.off(EVENTS.DEPOSIT_CONFIRMED, handleDepositConfirmed);
        realtimeService.off(EVENTS.BALANCE_UPDATED, handleBalanceUpdated);
      };
    });

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [user?.id, queryClient, refreshUserData, success, warning]);

  // Refresh balance when window regains focus (user returns from game)
  React.useEffect(() => {
    const handleFocus = async () => {
      if (user) {
        try {
          await refreshUserData();
          window.dispatchEvent(new Event('userBalanceUpdated'));
        } catch (error) {
          console.error('Error refreshing balance on focus:', error);
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, refreshUserData]);

  // Handle ForceLogout (usually sent when duplicate login is detected)
  React.useEffect(() => {
    // Always listen, but only act if user is logged in
    const handleForceLogout = (data: any) => {
      // Check if user is still logged in
      const isLoggedIn = !!localStorage.getItem('authToken');
      if (!isLoggedIn) {
        return;
      }

      // Check if deposit is in progress (popup window open) - skip force logout
      const depositInProgress = localStorage.getItem('depositInProgress');
      if (depositInProgress === 'true') {
        return;
      }

      // Set flag to prevent apiService from redirecting to /login
      localStorage.setItem('duplicateLoginHandled', 'true');
      
      // Show warning notification first
      warning('Duplicate Login Detected', 'You have been logged out because you logged in from another device.', 6000);
      
      // Clear auth data directly to avoid triggering other logout flows
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('allUserData');
      
      // Redirect to landing page immediately (before logout() to prevent redirect to /login)
      navigate('/', { replace: true });
      
      // Then call logout to update context state (but redirect already happened)
      setTimeout(() => {
        logout();
        // Clear the flag after a delay
        setTimeout(() => {
          localStorage.removeItem('duplicateLoginHandled');
        }, 1000);
      }, 100);
    };

    onForceLogout(handleForceLogout);

    return () => {
      // Note: onForceLogout doesn't have an off method, but the handler will be cleaned up
      // when the component unmounts
    };
  }, [onForceLogout, logout, navigate, warning]);

  // Handle duplicate login detection via PublicLogin - logout old device in real-time
  React.useEffect(() => {
    // Always listen, but only act if user is logged in
    const handlePublicLogin = (data: any) => {
      
      // Check if user is logged in
      const isLoggedIn = !!localStorage.getItem('authToken');
      if (!isLoggedIn || !user) {
        return;
      }

      // Check if deposit is in progress (popup window open) - skip duplicate login detection
      const depositInProgress = localStorage.getItem('depositInProgress');
      if (depositInProgress === 'true') {
        return;
      }

      // Check if this is a response to our own PublicLogin (within 2 second cooldown)
      const now = Date.now();
      const isOurOwnMessage = publicLoginSentTimeRef.current && (now - publicLoginSentTimeRef.current) < 2000;
      
      // If it's a response to our own PublicLogin (status confirmation), don't logout
      if (isOurOwnMessage) {
        return;
      }
      
      // If it's just a status response (status: 0 or 1) without message/deviceInfo, it's likely our own
      if (data.status !== undefined && (data.status === 0 || data.status === 1) && !data.message && !data.deviceInfo) {
        // Update ref to track this as our own message
        publicLoginSentTimeRef.current = Date.now();
        return;
      }
      
      // If we get here and user is logged in, and it's not our own message,
      // it means another device logged in - logout immediately
      
      // Set flag to prevent apiService from redirecting to /login
      localStorage.setItem('duplicateLoginHandled', 'true');
      
      // Show warning notification first
      warning('Duplicate Login Detected', 'You have been logged out because you logged in from another device.', 6000);
      
      // Clear auth data directly to avoid triggering other logout flows
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      localStorage.removeItem('allUserData');
      
      // Redirect to landing page immediately (before logout() to prevent redirect to /login)
      navigate('/', { replace: true });
      
      // Then call logout to update context state (but redirect already happened)
      setTimeout(() => {
        logout();
        // Clear the flag after a delay
        setTimeout(() => {
          localStorage.removeItem('duplicateLoginHandled');
        }, 1000);
      }, 100);
    };

    on('publicLogin', handlePublicLogin);

    return () => {
      off('publicLogin', handlePublicLogin);
    };
  }, [on, off, logout, navigate, user, warning]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const clearSidebarActive = () => {
    setSidebarActiveCategory('');
  };

  const setSidebarActive = (category: string) => {
    setSidebarActiveCategory(category);
  };

  const clearHeaderActive = () => {
    setHeaderActiveButton(null);
  };

  const setHeaderActive = (buttonId: string) => {
    setHeaderActiveButton(buttonId);
  };

  return (
    <>
      {/* Skip Link for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary-500 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:outline-none"
      >
        Skip to main content
      </a>
      <div className={`App custom-scrollbar min-h-screen flex flex-col overflow-x-hidden transition-all duration-300 ${isLiveChatOpen ? 'lg:mr-80 xl:mr-96' : ''}`}>
        {/* Fixed Header - Full width, above sidebar; hidden on mobile when live chat is open */}
        <div className={`fixed top-0 left-0 z-[1000] transition-all duration-300 ${isLiveChatOpen ? 'hidden lg:block lg:right-80 xl:right-96' : 'right-0'}`}>
          <Header 
            onScrollToSection={scrollToSection} 
            onToggleSidebar={toggleSidebar}
            onClearSidebarActive={clearSidebarActive}
            activeButton={headerActiveButton}
            onSetActiveButton={setHeaderActive}
          />
        </div>
        
        {/* Layout Container - Mobile padding varies based on auth state */}
        <div className={`flex pt-16`}>
          {/* Sidebar - Fixed position, starts from top-0, covers all components */}
          <div className={`fixed left-0 top-0 bottom-0 z-[999] transition-transform duration-300 ease-in-out ${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}>
            <Sidebar 
              activeCategory={sidebarActiveCategory}
              onSetActiveCategory={setSidebarActive}
              onClearHeaderActive={clearHeaderActive}
              onCloseSidebar={closeSidebar}
            />
          </div>
          
          {/* Overlay for mobile sidebar */}
          {isSidebarOpen && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[998] lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
            />
          )}
          
          {/* Main Content Area - Proper spacing for sidebar and bottom nav */}
          <main id="main-content" className="flex-1 lg:ml-80 custom-scrollbar overflow-x-hidden w-full max-w-full pb-16 lg:pb-0" role="main">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<LandingPage onScrollToSection={scrollToSection} />} />
                <Route path="/verify-email" element={<EmailVerificationPage />} />
                <Route path="/signup-complete" element={<SignupCompletePage />} />
                <Route path="/success-deposit" element={<DepositSuccessPage />} />
                <Route path="/cancel-deposit" element={<DepositCancelPage />} />
                <Route path="/deposit" element={<DepositPage />} />
                <Route path="/withdraw" element={<WithdrawPage />} />
                <Route path="/bet-history" element={<BetHistoryPage />} />
                <Route path="/my-page" element={<MyPage />} />
                <Route path="/rule" element={<NoticePage />} />
                <Route path="/casino" element={<LiveCasinoPage />} />
                <Route path="/slot" element={<SlotGamesPage />} />
                <Route path="/roulette" element={<RoulettePage />} />
                <Route path="/coupon" element={<CouponPage />} />
                <Route path="/bonus" element={<BonusPage />} />
                <Route path="/daily-check" element={<DailyCheckPage />} />
                <Route path="/message" element={<MessagePage />} />
                <Route path="/event" element={<EventPage />} />
                <Route path="/question" element={<QuestionPage />} />
                <Route path="/config-test" element={<RuntimeConfigTest />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/wallet" element={<WalletPage />} />
                <Route path="/minigame" element={<Navigate to="/minigame/builtin" replace />} />
                <Route path="/minigame/builtin" element={<MinigameBuiltinPage />} />
                <Route path="/minigame/free" element={<MinigameFreePage />} />
                <Route path="/minigame/btcgraph" element={<MinigameBtcGraphPage />} />
                <Route path="/minigame/game/:slug" element={<MinigameGamePage />} />
                <Route path="/play" element={<GamePlayPage />} />
                <Route path="/catchcoin" element={<CatchCoinPage />} />
                <Route path="/sports" element={<SportsBettingPage />} />
                <Route path="/live-sports" element={<LiveSportsBettingPage />} />
                <Route path="/esports" element={<EsportsBettingPage />} />
                <Route path="/live-esports" element={<LiveEsportsBettingPage />} />
                <Route path="/event/:fixtureId" element={<EventDetailPage />} />
              </Routes>
            </Suspense>
          </main>
        </div>
      </div>
      
      {/* Global Notification Container */}
      <NotificationContainer />
      
      {/* Customer Support Chat - Available for all users */}
      <CustomerSupportChat />
      
      {/* Live Chat Panel - Right side chat (fixed position) */}
      <LiveChatPanel />
      
      {/* Bottom Navigation - Mobile only */}
      <BottomNavigation onToggleSidebar={toggleSidebar} />
      
      {/* Bonus Claim Modal - Shows for logged in users with unclaimed bonuses */}
      {isAuthenticated && <BonusClaimModal />}
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppSupportChatWrapper>
            <LiveChatProvider>
              <LoginModalProvider>
                <BetSlipProvider>
                  <BtcPriceProvider>
                    <SocketProvider autoConnect={true}>
                      <Router>
                        <AppContent />
                      </Router>
                    </SocketProvider>
                  </BtcPriceProvider>
                </BetSlipProvider>
              </LoginModalProvider>
            </LiveChatProvider>
          </AppSupportChatWrapper>
        </AuthProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
    </ThemeProvider>
  );
}

export default App;
